import { CHART_SETTINGS_STORAGE_KEYS } from '@/lib/localStorageKeys/chart-settings';
import { CHART_SETTINGS_VERSION, DEFAULT_CHART_SETTINGS } from './default-data';
import { ChartSettings } from './types';

export const getStoredChartSettings = (): ChartSettings => {
  if (typeof window === 'undefined') {
    return DEFAULT_CHART_SETTINGS;
  }

  try {
    const storedData = localStorage.getItem(CHART_SETTINGS_STORAGE_KEYS.CORE);
    
    if (!storedData) {
      // If no settings exist, save and return defaults
      saveChartSettings(DEFAULT_CHART_SETTINGS);
      return DEFAULT_CHART_SETTINGS;
    }

    const parsedData = JSON.parse(storedData);

    // One-time re-base when shipped defaults change (settingsVersion bump):
    // pre-v2 installs carried the monochrome blackOnWhite scheme and a
    // volumes pane on by default — replace style + volumes flag with the new
    // defaults while keeping the user's other choices.
    if ((parsedData.settingsVersion ?? 1) < CHART_SETTINGS_VERSION) {
      const migrated: ChartSettings = {
        ...DEFAULT_CHART_SETTINGS,
        ...parsedData,
        chartStyle: { ...DEFAULT_CHART_SETTINGS.chartStyle },
        features: {
          ...DEFAULT_CHART_SETTINGS.features,
          ...parsedData.features,
          volumes: DEFAULT_CHART_SETTINGS.features.volumes,
        },
        settingsVersion: CHART_SETTINGS_VERSION,
      };
      saveChartSettings(migrated);
      return migrated;
    }

    // Merge with defaults to ensure all fields exist (including new features)
    return {
      ...DEFAULT_CHART_SETTINGS,
      ...parsedData,
      chartStyle: {
        ...DEFAULT_CHART_SETTINGS.chartStyle,
        ...parsedData.chartStyle,
      },
      features: {
        ...DEFAULT_CHART_SETTINGS.features,
        ...parsedData.features,
      },
    };
  } catch (error) {
    console.error('Error loading chart settings from localStorage:', error);
    return DEFAULT_CHART_SETTINGS;
  }
};

export const saveChartSettings = (settings: ChartSettings): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(CHART_SETTINGS_STORAGE_KEYS.CORE, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving chart settings to localStorage:', error);
  }
};

/**
 * Get the size of stored chart settings in bytes
 * @returns Size in bytes or 0 if not found
 */
export function getStoredChartSettingsValue(): number {
  if (typeof window === 'undefined') return 0;
  
  try {
    const stored = localStorage.getItem(CHART_SETTINGS_STORAGE_KEYS.CORE);
    
    if (!stored) return 0;
    
    // Calculate size in bytes (JavaScript strings are UTF-16, so each character is 2 bytes)
    return stored.length * 2;
  } catch (error) {
    console.error('Error getting chart settings size:', error);
    return 0;
  }
}

/**
 * Clear stored chart settings
 * @returns Success status
 */
export function clearStoredChartSettings(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    localStorage.removeItem(CHART_SETTINGS_STORAGE_KEYS.CORE);
    return true;
  } catch (error) {
    console.error('Error clearing chart settings:', error);
    return false;
  }
}